import * as Sentry from '@sentry/react'
import { ElementType, Suspense } from 'react'
import AppErrorFallback from '../components/AppErrorFallback'
import ContentLoading from '../components/ContentLoading'
import LoadingScreen from '../components/LoadingScreen'

// ----------------------------------------------------------------------
// Every screen is a chunk fetched on demand, so every screen has a moment
// before it exists, and a way to not exist at all. What is shown in either case
// is the difference between a route change and what looks like the application
// restarting.
//
//   `page`  the default. Fills the content area and leaves the sidebar and the
//           top bar standing, because they are already correct. A screen that
//           throws is replaced on its own, so the operator can navigate away
//           instead of reloading.
//   `app`   the boot screen, covering the viewport. Only for the chunks that
//           carry the chrome itself, where there is nothing to keep and nothing
//           left to navigate with.
//
// The boundary belongs here rather than only around the router: a chunk whose
// hashed filename no longer exists after a deploy is the ordinary case, and
// taking the whole console down for one screen is the thing this file exists to
// stop.
// ----------------------------------------------------------------------

export type LoadableFallback = 'app' | 'page'

interface LoadableOptions {
  fallback?: LoadableFallback
}

const Loadable =
  (Component: ElementType, { fallback = 'page' }: LoadableOptions = {}) =>
  (props: any) => {
    const isApp = fallback === 'app'

    return (
      <Sentry.ErrorBoundary
        fallback={<AppErrorFallback variant={isApp ? 'app' : 'screen'} />}
      >
        <Suspense fallback={isApp ? <LoadingScreen /> : <ContentLoading />}>
          <Component {...props} />
        </Suspense>
      </Sentry.ErrorBoundary>
    )
  }

export default Loadable
